import { useTranslation } from 'react-i18next';

import { ResetPasswordModalElement, UpdateProfileModalElement } from '@shared/dream-components';

import { useAccountProfileContext } from '~/context/SubscriptionManagement';

import ResetPasswordAction from '../actions/ResetPasswordAction';
import UpdateProfileAction from '../actions/UpdateProfileAction';
import { ConnectedAccountsModalContainer } from '../ConnectedAccountsModal/ConnectedAccountsModalContainer';

/**
 * Renders the account modals — connected-accounts (OAuth), edit-profile, and set/reset-password —
 * reusing the existing render-prop Action wrappers. Profile/password inputs come from
 * `AccountProfileProvider`; the connected-accounts modal sources its own data from
 * `IdentitiesProvider`. The connected-accounts modal is always mounted (it can be opened via the
 * `?modal=connected_accounts` deeplink); the edit/reset modals render only for logged-in subscribers.
 */
export const AccountProfileModals = () => {
  const { t } = useTranslation();
  const {
    isLoggedIn,
    email,
    firstName,
    lastName,
    avatarUrl,
    subscriberId,
    currentModal,
    closeAccountModal,
    hasEmailChangePending,
    hasSetPassword,
    onResend,
    isResendingConfirmationEmail,
  } = useAccountProfileContext();

  return (
    <>
      <ConnectedAccountsModalContainer />

      {isLoggedIn && (
        <div className="absolute">
          <UpdateProfileAction
            currentSubscriberEmail={email}
            currentSubscriberFirstName={firstName ?? ''}
            currentSubscriberLastName={lastName ?? ''}
            subscriberId={subscriberId}
            onSuccess={closeAccountModal}
          >
            {({ isLoading, onAvatarSelect }) => (
              <UpdateProfileModalElement
                isLoading={isLoading || isResendingConfirmationEmail}
                isOpen={currentModal === 'edit_profile'}
                onOpenChange={(_o: boolean) => closeAccountModal()}
                onCancel={closeAccountModal}
                canResendEmail={hasEmailChangePending}
                onClickResendEmail={onResend}
                onAvatarSelect={onAvatarSelect}
                element={{
                  type: 'updateProfileModal',
                  attrs: {
                    data: {
                      email,
                      firstName,
                      lastName,
                      avatarUrl,
                    },
                  },
                }}
                translations={{
                  'manage.update_profile_modal.title': t('manage.update_profile_modal.title'),
                  'manage.update_profile_modal.description': t('manage.update_profile_modal.description'),
                  'manage.update_profile_modal.form.email_label': t('manage.update_profile_modal.form.email_label'),
                  'manage.update_profile_modal.form.first_name_label': t(
                    'manage.update_profile_modal.form.first_name_label',
                  ),
                  'manage.update_profile_modal.form.last_name_label': t(
                    'manage.update_profile_modal.form.last_name_label',
                  ),
                  'manage.update_profile_modal.form.email_placeholder': t(
                    'manage.update_profile_modal.form.email_placeholder',
                  ),
                  'manage.update_profile_modal.form.first_name_placeholder': t(
                    'manage.update_profile_modal.form.first_name_placeholder',
                  ),
                  'manage.update_profile_modal.form.last_name_placeholder': t(
                    'manage.update_profile_modal.form.last_name_placeholder',
                  ),
                  'manage.update_profile_modal.cancel': t('manage.update_profile_modal.cancel'),
                  'manage.update_profile_modal.save': t('manage.update_profile_modal.save'),
                  'manage.update_profile_modal.form.email_change_helper_text': t(
                    'manage.update_profile_modal.form.email_change_helper_text',
                  ),
                  'manage.update_profile_modal.resend_email': t('manage.update_profile_modal.resend_email'),
                  'manage.update_profile_modal.form.avatar_alt': t('manage.update_profile_modal.form.avatar_alt'),
                  'manage.update_profile_modal.form.avatar_helper_text': t(
                    'manage.update_profile_modal.form.avatar_helper_text',
                  ),
                }}
              >
                {null}
              </UpdateProfileModalElement>
            )}
          </UpdateProfileAction>
          <ResetPasswordAction onSuccess={closeAccountModal}>
            {({ isLoading }) => (
              <ResetPasswordModalElement
                isLoading={isLoading}
                isOpen={currentModal === 'reset_password'}
                onOpenChange={(_o: boolean) => closeAccountModal()}
                onCancel={closeAccountModal}
                element={{
                  type: 'resetPasswordModal',
                  attrs: {},
                }}
                hasSetPassword={hasSetPassword}
                translations={{
                  'manage.password.title': t('manage.password.title'),
                  'manage.password.set_title': t('manage.password.set_title'),
                  'manage.password.set_subtitle': t('manage.password.set_subtitle'),
                  'manage.password.form.password_label': t('manage.password.form.password_label'),
                  'manage.password.form.password_placeholder': t('manage.password.form.password_placeholder'),
                  'manage.password.form.requirements_text': t('manage.password.form.requirements_text'),
                  'manage.password.form.confirm_password_label': t('manage.password.form.confirm_password_label'),
                  'manage.password.form.confirm_password_placeholder': t(
                    'manage.password.form.confirm_password_placeholder',
                  ),
                  'shared.cta.cancel': t('shared.cta.cancel'),
                  'shared.cta.submit': t('shared.cta.submit'),
                }}
              >
                {null}
              </ResetPasswordModalElement>
            )}
          </ResetPasswordAction>
        </div>
      )}
    </>
  );
};
